import { Box, Popover, TextField, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { BehaviorConfigOption, useSelectedPartner } from 'src/graphql';
import { CustomViewFormConditionItem, UIType } from 'src/graphql/entities/custom-view';
import { isMulti } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { mapValuesToOptions } from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { formatConditionValue } from 'src/graphql/entities/custom-view/custom-view-value-format';
import { RulesDatePicker } from 'src/pages/Settings/rules/components/rules-date-picker';
import { QueryOptionsValue } from 'src/pages/Settings/rules/use-rules-engine';
import { FilterSection } from './styles';

const toOptionKey = (option: BehaviorConfigOption) => String(option.value);

const isSameOption = (a: BehaviorConfigOption, b: BehaviorConfigOption) =>
  toOptionKey(a).toLowerCase() === toOptionKey(b).toLowerCase();

interface ValueSelectorProps {
  condition: CustomViewFormConditionItem;
  onChange: (v: CustomViewFormConditionItem['value']) => void;
  anchorEl: HTMLElement | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  optionsSource?: QueryOptionsValue;
}

export const ValueSelector = ({
  condition,
  onChange,
  anchorEl,
  open,
  setOpen,
  optionsSource,
}: ValueSelectorProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const { selectedPartner } = useSelectedPartner();

  const { field, value: rawValue } = condition;

  /* options that are currently known (or fetched dynamically) */
  const optionsData = useMemo(
    () => (!field ? { options: [] as BehaviorConfigOption[] } : optionsSource),
    [field, optionsSource],
  );

  const selectorOptions = useMemo<BehaviorConfigOption[]>(() => {
    if (optionsData?.options?.length) {
      return optionsData.options;
    }
    return (field?.fieldOptions ?? []).map(({ id, label, value }) => ({ id, label, value }));
  }, [field, optionsData]);

  const valueForList = useMemo((): CustomViewFormConditionItem['value'] => {
    if (!Array.isArray(rawValue)) return rawValue;
    const allOptions = (optionsData?.historicalOptions ?? selectorOptions).map((option) => ({
      ...option,
      id: option.id ? String(option.id) : String(option.value),
    }));

    return mapValuesToOptions(rawValue, allOptions) as CustomViewFormConditionItem['value'];
  }, [rawValue, optionsData, selectorOptions]);

  const pickedOptions = useMemo(
    () => (Array.isArray(valueForList) ? (valueForList as BehaviorConfigOption[]) : []),
    [valueForList],
  );

  // Picked values that a server search has pushed out of the options stay listed, so they can be unpicked.
  const listOptions = useMemo(
    () => [
      ...selectorOptions,
      ...pickedOptions.filter(
        (picked) => !selectorOptions.some((option) => isSameOption(option, picked)),
      ),
    ],
    [pickedOptions, selectorOptions],
  );

  const menuOptions = useMemo<SelectMenuOption[]>(
    () => listOptions.map((option) => ({ value: toOptionKey(option), title: option.label })),
    [listOptions],
  );

  const pickedKeys = listOptions
    .filter((option) => pickedOptions.some((picked) => isSameOption(option, picked)))
    .map(toOptionKey);

  const isListField = field?.uiType === UIType.List;
  const searchesOnServer = Boolean(optionsData?.onInputChange);

  /* for primitive + date */
  const flatValue =
    typeof valueForList === 'string'
      ? valueForList
      : ((valueForList as { value: string } | undefined)?.value ?? null);

  const isMultiple = isMulti(condition.operator);

  const sectionSx = { maxWidth: '12rem', minWidth: 0, flexShrink: 1, overflow: 'hidden' };

  const valueLabel = (
    <Typography
      variant="body2"
      sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
    >
      {formatConditionValue(condition, optionsSource?.historicalOptions ?? selectorOptions)}
    </Typography>
  );

  if (isListField) {
    return (
      <SelectMenu
        label={field.label}
        options={menuOptions}
        multiple={isMultiple}
        value={isMultiple ? undefined : (pickedKeys[0] ?? null)}
        values={isMultiple ? pickedKeys : undefined}
        open={open}
        onOpenChange={setOpen}
        menuWidth="18.75rem"
        searchValue={searchesOnServer ? inputValue : undefined}
        onSearchChange={
          searchesOnServer
            ? (query) => {
                setInputValue(query);
                optionsData?.onInputChange?.(query);
              }
            : undefined
        }
        loading={optionsData?.isLoading}
        onChange={(key) => {
          const option = listOptions.find((item) => toOptionKey(item) === key);
          if (option) {
            onChange([option] as unknown as CustomViewFormConditionItem['value']);
          }
        }}
        onValuesChange={(keys) =>
          onChange(
            listOptions.filter((item) =>
              keys.includes(toOptionKey(item)),
            ) as unknown as CustomViewFormConditionItem['value'],
          )
        }
        renderTrigger={({ getTriggerProps }) => (
          <FilterSection {...getTriggerProps()} role="button" tabIndex={0} sx={sectionSx}>
            {valueLabel}
          </FilterSection>
        )}
      />
    );
  }

  return (
    <>
      <FilterSection onClick={() => setOpen(true)} sx={sectionSx}>
        {valueLabel}
      </FilterSection>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          {/* DATE — RulesDatePicker supports rolling and relative date ranges (like "last 7 days"), bringing it in line with the mobile wizard and desktop query builder. Since it's a multi-step flow, don't close the popover on change like we did with the old DatePicker. */}
          {field?.uiType === UIType.Date && (
            <RulesDatePicker
              value={flatValue ?? ''}
              timezone={selectedPartner?.timeZone}
              onChange={({ isRollingDate, value }) =>
                onChange(isRollingDate ? (value as string) : (value as Dayjs).toISOString())
              }
            />
          )}

          {/* STRING / NUMBER */}
          {field?.uiType !== UIType.Date && field?.uiType !== UIType.List && (
            <TextField
              fullWidth
              label={field!.label}
              size="small"
              value={flatValue ?? ''}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => setOpen(false)}
              autoFocus
            />
          )}
        </Box>
      </Popover>
    </>
  );
};
